export interface FaqTreeFaq {
  id: number;
  question: string;
}

export interface FaqTreeTopic {
  id: number;
  title: string;
  children: FaqTreeTopic[];
  faqs: FaqTreeFaq[];
}

interface RawTopic { id: number; title: string; parent: number | null; }
interface RawFaq { id: number; question: string; topics: number[]; }

export function buildFaqTree(topics: RawTopic[], faqs: RawFaq[]): FaqTreeTopic[] {
  const nodes: { [id: number]: FaqTreeTopic } = {};
  (topics || []).forEach((t) => {
    nodes[t.id] = { id: t.id, title: t.title, children: [], faqs: [] };
  });
  (faqs || []).forEach((f) => {
    (f.topics || []).forEach((topicId) => {
      if (nodes[topicId]) {
        nodes[topicId].faqs.push({ id: f.id, question: f.question });
      }
    });
  });
  const roots: FaqTreeTopic[] = [];
  (topics || []).forEach((t) => {
    if (t.parent !== null && t.parent !== undefined && nodes[t.parent]) {
      nodes[t.parent].children.push(nodes[t.id]);
    } else {
      roots.push(nodes[t.id]);
    }
  });
  return roots;
}

export function isLeaf(node: FaqTreeTopic): boolean {
  return node.children.length === 0;
}

export function subtreeFaqIds(node: FaqTreeTopic): number[] {
  const ids = new Set<number>();
  const walk = (n: FaqTreeTopic) => {
    n.faqs.forEach((f) => ids.add(f.id));
    n.children.forEach(walk);
  };
  walk(node);
  return Array.from(ids);
}

export function topicCheckState(node: FaqTreeTopic, selected: Set<number>): 'checked' | 'indeterminate' | 'empty' {
  const ids = subtreeFaqIds(node);
  let state: 'checked' | 'indeterminate' | 'empty' = 'empty';
  if (ids.length > 0) {
    const selectedCount = ids.filter((id) => selected.has(id)).length;
    if (selectedCount === ids.length) {
      state = 'checked';
    } else if (selectedCount > 0) {
      state = 'indeterminate';
    }
  }
  return state;
}

export function leafSelectedCount(node: FaqTreeTopic, selected: Set<number>): number {
  return node.faqs.filter((f) => selected.has(f.id)).length;
}

export function parentSelectedSubtopicCount(node: FaqTreeTopic, selected: Set<number>): number {
  return node.children.filter((child) => subtreeFaqIds(child).some((id) => selected.has(id))).length;
}

export function findTopic(nodes: FaqTreeTopic[], id: number): FaqTreeTopic {
  let result: FaqTreeTopic = null;
  nodes.forEach((node) => {
    if (result === null) {
      if (node.id === id) {
        result = node;
      } else {
        const found = findTopic(node.children, id);
        if (found !== null) {
          result = found;
        }
      }
    }
  });
  return result;
}
